import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Input, Icon, Tooltip } from 'antd';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import useHousingStockMatch from '../../../../hooks/useHousingStockMatch';
import { getQueryKeys } from '../../../../queries/queryKeys';
import { links } from '../../../../constants/links';
import { texts } from '../../../../constants/texts';
import CheckMarkMenu from '../../../../../../components/CheckMarkMenu/CheckMarkMenu';
import SimpleButton from '../../../common/SimpleButton/SimpleButton';
import { getReplacedString } from '../../../../../../helpers/getReplacedString';
import { setHousesTablePage } from '../../../../store/actions/housingStockActions';
import { saveSelectedCompanyId } from '../../../../../../helpers/companyStorage';
import styles from '../MainHeader.module.scss';

export const SelectCompanyBase = ({
  currentCompany,
  companies,
  onChange,
  btnProps = {},
  disabled,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const textRef = useRef();

  const handleMenuClick = (item) => {
    if (disabled) {
      return;
    }

    const companyId = item.id;

    onChange({ item, companyId });

    setDropdownVisible(false);
  };

  const handleVisibleChange = (visible) => {
    setDropdownVisible(disabled ? false : visible);
  };

  const handleSearch = (e) => {
    const val = e.target.value;

    const value = getReplacedString(val);

    const filteredCompanies = companies.filter((company) => {
      const companyName = getReplacedString(company.name);

      return companyName.includes(value);
    });

    setFilteredCompanies(filteredCompanies);
    setSearchValue(val);
  };

  useEffect(() => {
    if (companies?.length > 0) {
      setFilteredCompanies(companies);
    }
  }, [companies]);

  useEffect(() => {
    if (!dropdownVisible) {
      setSearchValue('');
      setFilteredCompanies(companies || []);
    }
  }, [dropdownVisible, companies]);

  useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      setShowTooltip(element.offsetWidth < element.scrollWidth);
    };

    window.addEventListener('resize', checkOverflow);
    checkOverflow();
    return () => window.removeEventListener('resize', checkOverflow);
  }, [currentCompany]);

  return (
    <Dropdown
      trigger={['click']}
      visible={dropdownVisible}
      onVisibleChange={handleVisibleChange}
      placement="bottomRight"
      overlay={
        <CheckMarkMenu
          items={filteredCompanies}
          selectedItems={[currentCompany?.id]}
          renderContent={(item) => item.name}
          visible={dropdownVisible}
          renderSearchInput={({ inputRef }) => (
            <Input
              ref={inputRef}
              onChange={handleSearch}
              value={searchValue}
            />
          )}
          handlers={{
            onMenuClick: handleMenuClick,
          }}
        />
      }
    >
      <Tooltip
        title={showTooltip && (currentCompany?.name || texts.managementCompany)}
      >
        <SimpleButton
          text={
            <span
              className={styles.btnText}
              ref={textRef}
            >
              {currentCompany?.name || texts.managementCompany}
            </span>
          }
          icon={<Icon type="caret-down" />}
          {...btnProps}
        />
      </Tooltip>
    </Dropdown>
  );
};

const SelectCompany = () => {
  const queryClient = useQueryClient();

  const match = useHousingStockMatch();

  const history = useHistory();
  const dispatch = useDispatch();

  const currentCompany = useSelector(
    (state) => state.housingStockStore.currentCompany
  );

  const companies = queryClient.getQueryData(getQueryKeys.companies());

  const handleChange = ({ companyId }) => {
    if (companyId !== currentCompany?.id) {
      saveSelectedCompanyId(companyId);

      history.push(
        `${links.rootWithCompany}/${companyId}/${match?.params.page}`
      );
      dispatch(setHousesTablePage(0));
    }
  };
  return (
    <SelectCompanyBase
      currentCompany={currentCompany}
      companies={companies}
      onChange={handleChange}
      btnProps={{
        className: styles.companiesDropdownBtn,
      }}
    />
  );
};

export default SelectCompany;
